import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconCarouselArrowLeft from "../../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../../img/icons/carousel-arrow-right.svg";
import { RichText } from "../../../common/RichText";
import {
    IProductID,
    ISafeHTML,
    IWebPageURL,
    isoSafeHTML,
} from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IProductTileAttributeRow,
    ITileID,
    isoTileID,
} from "../../../models/product-compare";
import { BreakPoint, IViewport } from "../../../models/screen";
import { ProductCompareTileAttributeRow } from "../elements/ProductCompareTileAttributeRow";
import { ProductCompareTileHeader } from "../elements/ProductCompareTileHeader";
import { getSelectedVariant } from "../selectors";

interface IProps {
    header: ISafeHTML;
    tiles: ICompareTile[];
    basketLink?: IWebPageURL;
    financingLink?: IWebPageURL;
    preselectedSize?: string | null;
    sizeAttributeOptionGroup?: IAttributeOptionGroup | null;
    priceSelectValue: string;
    pageRootProduct?: IProductID | null;
    urlParams?: URLSearchParams;
    viewport: IViewport;
}

const parseSelectedTilesFromURL = (
    urlParams: URLSearchParams,
    allTiles: ICompareTile[],
): { selectedTileIDs: ITileID[]; selectedTiles: ICompareTile[] } => {
    const selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
        .split(",")
        .map(Number)
        .map(isoTileID.wrap);
    const selectedTiles: ICompareTile[] = allTiles.filter((tile) =>
        selectedTileIDs.includes(tile.product_compare_tile.id),
    );

    return {
        selectedTileIDs,
        selectedTiles,
    };
};

const calculateTilesPerSlide = (viewportWidth: number): number => {
    return viewportWidth > BreakPoint.LARGE ? 5 : 2;
};

const buildViewableTiles = (
    selectedTiles: ICompareTile[],
    currentPosition: number,
    tilesPerSlide: number,
): ICompareTile[] => {
    const increment = currentPosition + tilesPerSlide;

    if (selectedTiles.length - currentPosition === 1) {
        return selectedTiles.slice(currentPosition - 1, increment);
    }
    return selectedTiles.slice(currentPosition, increment);
};

const buildHeaderContent = (
    header: ISafeHTML,
    urlParams: URLSearchParams | undefined,
    selectedTileIDs: ITileID[],
): ISafeHTML => {
    if (!urlParams) {
        return header;
    }

    let href = decodeURIComponent(urlParams.get("return_url") || "");
    if (!href) {
        return header;
    }

    if (urlParams.get("tiles")) {
        href = `${href}?tiles=${encodeURIComponent(selectedTileIDs.join(","))}`;
    }
    const link = `<a class="al-compareresults-changeselection" href="${href}">${t`Add/Change Selection`}</a>`;
    return isoSafeHTML.wrap(`${header}${link}`);
};

const buildAttributeRow = (
    tile: ICompareTile,
    row: IProductTileAttributeRow,
    basketLink: IWebPageURL | undefined,
    financingLink: IWebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return (
        <ProductCompareTileAttributeRow
            key={`${tile.product_compare_tile.id}-${row.row_type.id}`}
            row={row}
            data={tile}
            basketLink={basketLink}
            financingLink={financingLink}
            priceSelectValue={priceSelectValue}
            sizeAttributeOptionGroup={sizeAttributeOptionGroup || null}
        />
    );
};

const buildChildRows = (
    tile: ICompareTile,
    rows: ReadonlyArray<IProductTileAttributeRow>,
    basketLink: IWebPageURL | undefined,
    financingLink: IWebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return rows.map((row: IProductTileAttributeRow) => {
        return (
            <div
                className={`child-row product-attribute-row ${row.row_type.row_type}`}
                key={`${row.row_type.id}-child-row`}
            >
                {buildAttributeRow(
                    tile,
                    row,
                    basketLink,
                    financingLink,
                    priceSelectValue,
                    sizeAttributeOptionGroup,
                )}
            </div>
        );
    });
};

const buildAttributeRows = (
    tile: ICompareTile,
    basketLink: IWebPageURL | undefined,
    financingLink: IWebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return tile.rows.map((row: IProductTileAttributeRow) => {
        return (
            <div
                key={row.row_type.id}
                className={`product-attribute-row ${row.row_type.row_type}`}
            >
                <h4>{row.row_type.title}</h4>
                <div>
                    {buildAttributeRow(
                        tile,
                        row,
                        basketLink,
                        financingLink,
                        priceSelectValue,
                        sizeAttributeOptionGroup,
                    )}
                    {buildChildRows(
                        tile,
                        row.children,
                        basketLink,
                        financingLink,
                        priceSelectValue,
                        sizeAttributeOptionGroup,
                    )}
                </div>
            </div>
        );
    });
};

const buildCompareTiles = (
    viewableTiles: ICompareTile[],
    priceSelectValue: string,
    pageRootProduct: IProductID | null | undefined,
    basketLink: IWebPageURL | undefined,
    financingLink: IWebPageURL | undefined,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return viewableTiles.map((tile: ICompareTile) => {
        const variant = getSelectedVariant(tile, priceSelectValue);
        return (
            <div className="tile" key={`${tile.product_compare_tile.id}`}>
                <ProductCompareTileHeader
                    data={tile}
                    selectedVariant={variant}
                    pageRootProduct={pageRootProduct || null}
                    showRating={false}
                />
                {buildAttributeRows(
                    tile,
                    basketLink,
                    financingLink,
                    priceSelectValue,
                    sizeAttributeOptionGroup,
                )}
            </div>
        );
    });
};

const renderSlideControls = (
    viewportWidth: number,
    selectedTilesLength: number,
    currentPosition: number,
    tilesPerSlide: number,
    onPrevSlideClick: () => void,
    onNextSlideClick: () => void,
): React.ReactNode => {
    if (viewportWidth > BreakPoint.LARGE) {
        return null;
    }
    if (selectedTilesLength <= 2) {
        return null;
    }
    return (
        <nav>
            <button
                aria-label={t`Previous`}
                disabled={currentPosition === 0}
                onClick={onPrevSlideClick}
            >
                <SVG aria-hidden="true" src={iconCarouselArrowLeft} />
            </button>
            <button
                aria-label={t`next`}
                disabled={
                    currentPosition + tilesPerSlide >= selectedTilesLength
                }
                onClick={onNextSlideClick}
            >
                <SVG aria-hidden="true" src={iconCarouselArrowRight} />
            </button>
        </nav>
    );
};

export const ProductCompareGrid__ColumnCard_Component = (
    props: IProps,
): React.ReactNode => {
    const [currentPosition, setCurrentPosition] = React.useState(0);
    const [selectedTileIDs, setSelectedTileIDs] = React.useState<ITileID[]>([]);
    const [selectedTiles, setSelectedTiles] = React.useState<ICompareTile[]>(
        props.tiles,
    );
    const [tilesPerSlide, setTilesPerSlide] = React.useState(
        calculateTilesPerSlide(props.viewport.width),
    );

    const compareTiles = React.useRef<HTMLDivElement>(null);

    // Initialize selected tiles from URL params or use all tiles
    React.useEffect(() => {
        if (props.urlParams && props.urlParams.get("tiles")) {
            const { selectedTileIDs, selectedTiles } =
                parseSelectedTilesFromURL(props.urlParams, props.tiles);
            setSelectedTileIDs(selectedTileIDs);
            setSelectedTiles(selectedTiles);
        } else {
            const tileIDs = props.tiles.map(
                (tile: ICompareTile) => tile.product_compare_tile.id,
            );
            setSelectedTileIDs(tileIDs);
        }
    }, []);

    // Handle viewport resize
    React.useEffect(() => {
        const _window = getWindow();
        if (!_window) {
            return;
        }

        const handleResize = (): void => {
            setTilesPerSlide(calculateTilesPerSlide(props.viewport.width));
        };

        handleResize();
        _window.addEventListener("resize", handleResize);
        return () => {
            _window.removeEventListener("resize", handleResize);
        };
    }, [props.viewport.width]);

    const onPrevSlideClick = (): void => {
        setCurrentPosition(currentPosition - 1);
    };

    const onNextSlideClick = (): void => {
        setCurrentPosition(currentPosition + 1);
    };

    const viewableTiles = buildViewableTiles(
        selectedTiles,
        currentPosition,
        tilesPerSlide,
    );

    return (
        <>
            <header className="product-compare-grid-block-header">
                <RichText
                    html={buildHeaderContent(
                        props.header,
                        props.urlParams,
                        selectedTileIDs,
                    )}
                />
            </header>
            {renderSlideControls(
                props.viewport.width,
                selectedTiles.length,
                currentPosition,
                tilesPerSlide,
                onPrevSlideClick,
                onNextSlideClick,
            )}
            <div className="product-compare-tiles" ref={compareTiles}>
                {buildCompareTiles(
                    viewableTiles,
                    props.priceSelectValue,
                    props.pageRootProduct,
                    props.basketLink,
                    props.financingLink,
                    props.sizeAttributeOptionGroup,
                )}
            </div>
        </>
    );
};
